// BRN-2123 AC2 — BRAIN's operator-viewport surface registry.
//
// THE ONE TRUTH. The front end authors this because this is where the routes and the view models
// live; `viewport-declaration.ts` derives the registered declaration from it and
// `scripts/generate-viewport-declaration.mjs` writes that derivative into the backend's classpath
// resource, where the tool-catalog registrar picks it up. A second, hand-kept list of surface ids in
// the backend is exactly the drift AC2 exists to catch, so there is none.
//
// COVERAGE IS MEASURED, NOT MAINTAINED BY MEMORY. `surface-registry.spec.ts` compares every entry
// here against `runner/code-route-inventory.ts`'s parse of this app's own Angular router tree —
// including its cross-file `loadChildren` lazy mounts, which is exactly why a hand-rolled
// enumerator in this repo would be both wrong and redundant. A portal route with no surface fails,
// and a surface matching no route fails too.
//
// SENSITIVE MEANS "no highlights", and it is a product judgement made from what the screen actually
// renders — read, not guessed. `utility-provider-logins` renders the login accounts this
// organisation holds with its utility providers ("Add provider login", a per-provider credential
// table); the four screens under `settings/users` render people, their permissions, and what they
// did. A highlight is a label/value pair chosen for prominence, so on those screens it would be a
// credential or a person, travelling into a channel a model may quote back.

import type { ViewportEntityType, ViewportSurface } from './surface-registry-types';

/** AC2's "portal route": this product's authenticated portal tree. The onboarding wizard, the
 *  session repair screen, the public pages and the auth screens are outside it and declare no
 *  surface. `/portal` itself is a redirect and is classified out by the route inventory. */
export const PORTAL_ROUTE_PREFIX = '/portal';

/** The declaration's version, bumped when the SHAPE of a declaration changes — not when a surface
 *  is added, which is ordinary content movement the engine absorbs by replacement. */
export const VIEWPORT_DECLARATION_VERSION = 1;

// `toolArgument` names, for each type this product's own tool catalog can open, the lookup that takes an identifier
// of it and the argument it takes it under, read from that tool's `tool-schema.json`. It is what the engine's
// tool-slate ranking prefers when the operator's view carries that type (api-contract.md §4, §5). A type no catalog
// tool takes an identifier of declares none. The backend registrar test holds every entry to the catalog the same
// registration carries, and fails when a catalog tool takes a type's own identifier while the type names nothing.
//
// Two arguments are spelled as the trailing words of their type's own kind, deliberately, because that is how this
// catalog's lookups spell them: `facility.group.lookup` takes `groupId`, and its own relationship text states that
// `groupId` and `facilityGroupId` are the same group id; the provider tools take `providerId`.
export const VIEWPORT_ENTITY_TYPES: readonly ViewportEntityType[] = [
  {
    type: 'facility',
    description: 'A site whose energy use, cost and emissions this organisation tracks',
    toolArgument: { tool: 'facility.lookup', argument: 'facilityId' },
  },
  {
    type: 'facility-group',
    description: 'A named grouping of facilities reported and budgeted together',
    toolArgument: { tool: 'facility.group.lookup', argument: 'groupId' },
  },
  { type: 'utility-bill', description: 'A bill received from a utility for one facility and period' },
  {
    type: 'utility-provider',
    description: 'A utility this organisation buys energy or water from',
    toolArgument: { tool: 'provider.lookup', argument: 'providerId' },
  },
  { type: 'utility-provider-login', description: 'An account this organisation holds with a utility provider' },
  { type: 'utility-rate', description: 'A tariff a facility is billed under' },
  {
    type: 'conversion-factor',
    description: 'A unit-conversion or emission constant this organisation bills and reports with, and the platform default it inherits',
  },
  {
    type: 'custom-field',
    description: 'A field this organisation defined for itself, and what kind of record it applies to',
  },
  { type: 'budget', description: 'A planned spend for a facility or a group over a period' },
  { type: 'report', description: 'A saved report and the period and scope it covers' },
  { type: 'alert-rule', description: 'A rule that raises a condition when a measure moves' },
  { type: 'charging-station', description: 'An electric-vehicle charging station reporting usage' },
  { type: 'emissions-scope', description: 'A category of reported greenhouse-gas emissions' },
  { type: 'steam-meter', description: 'A meter recording steam delivered to a facility' },
  { type: 'live-feed-source', description: 'A source delivering interval readings in near real time' },
  { type: 'customer-payment', description: 'A payment received from a customer of this organisation' },
  { type: 'contract', description: 'A supply contract covering one or more facilities' },
  { type: 'user', description: 'A person with access to this product for this organisation' },
  { type: 'responsible-official', description: 'A person accountable for a facility or a report' },
  { type: 'audit-event', description: 'One recorded action taken in this product' },
  { type: 'tutorial', description: 'Guidance material published to operators of this product' },
];

export const VIEWPORT_SURFACES: readonly ViewportSurface[] = [
  {
    id: 'alerting',
    label: 'Alerting',
    description: 'The alert rules this organisation has configured, with their current raising state.',
    routePattern: '/portal/alerting',
    entityTypes: ['alert-rule', 'facility'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'alerting-advanced',
    label: 'Advanced alerting',
    description: 'Alert rules expressed against derived measures and multi-condition thresholds.',
    routePattern: '/portal/alerting/advanced',
    entityTypes: ['alert-rule', 'facility', 'facility-group'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'alerting-basic',
    label: 'Basic alerting',
    description: 'Single-threshold alert rules on a facility measure, and who is notified when one raises.',
    routePattern: '/portal/alerting/basic',
    entityTypes: ['alert-rule', 'facility'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'estream-analysis',
    label: 'Circuit-level analysis',
    description: 'Circuit-level measurement for facilities monitored by the sibling analysis product.',
    routePattern: '/portal/brain-estream-analysis',
    entityTypes: ['facility', 'live-feed-source'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'waves-identifiers',
    label: 'Device identifiers',
    description: 'The device identifiers this organisation has registered for its monitored equipment.',
    routePattern: '/portal/brain-waves-ids',
    entityTypes: ['facility', 'live-feed-source'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'budgeting',
    label: 'Budgeting',
    description: 'Planned against actual spend across the portfolio for the selected period.',
    routePattern: '/portal/budgeting',
    entityTypes: ['budget', 'facility', 'facility-group'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'budgeting-facilities',
    label: 'Facility budgets',
    description: 'Facility budgets for the selected period, with variance against actual spend.',
    routePattern: '/portal/budgeting/facilities',
    entityTypes: ['budget', 'facility'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'budgeting-facility',
    label: 'Facility budget',
    description: 'One facility budget: its period, its planned spend and how actual cost has tracked against it.',
    routePattern: '/portal/budgeting/facility/{facilityId}',
    entityTypes: ['budget', 'facility'],
    routeEntityTypes: ['facility'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'budgeting-group',
    label: 'Group budget',
    description: 'One group budget: its period, its planned spend and how the facilities in it have tracked.',
    routePattern: '/portal/budgeting/group/{groupId}',
    entityTypes: ['budget', 'facility-group', 'facility'],
    routeEntityTypes: ['facility-group'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'budgeting-groups',
    label: 'Group budgets',
    description: 'Group budgets for the selected period, with variance against actual spend.',
    routePattern: '/portal/budgeting/groups',
    entityTypes: ['budget', 'facility-group'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'comparison',
    label: 'Comparison',
    description: 'Facilities or groups compared side by side over a chosen period and measure.',
    routePattern: '/portal/comparison',
    entityTypes: ['facility', 'facility-group'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'contract-number',
    label: 'Contracts',
    description: 'The supply contracts covering this organisation, with the facilities they apply to.',
    routePattern: '/portal/contract-number',
    entityTypes: ['contract', 'facility', 'utility-provider'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'customer-payments',
    label: 'Customer payments',
    description: 'Payments received from the customers of this organisation, with the bills they settled.',
    routePattern: '/portal/customer-payments',
    entityTypes: ['customer-payment', 'facility'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'group-dashboard',
    label: 'Group dashboard',
    description: 'One facility group at a glance: its energy, cost and emissions for the selected period.',
    routePattern: '/portal/dashboard/group/{facilityGroupId}',
    entityTypes: ['facility-group', 'facility'],
    routeEntityTypes: ['facility-group'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'emissions',
    label: 'Emissions',
    description: 'Reported greenhouse-gas emissions by scope, facility and period.',
    routePattern: '/portal/emissions',
    entityTypes: ['emissions-scope', 'facility', 'facility-group'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'energy-star',
    label: 'Benchmarking',
    description: 'External benchmark scores for facilities, and the submissions behind them.',
    routePattern: '/portal/energy-star',
    entityTypes: ['facility', 'report'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'facility-new',
    label: 'Add a facility',
    description: 'The new-facility form: where a facility is, the group it belongs to and the energy it uses.',
    routePattern: '/portal/facilities/new',
    entityTypes: ['facility', 'facility-group'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'groups',
    label: 'Groups',
    description: 'The facility groups this organisation reports and budgets on.',
    routePattern: '/portal/groups',
    entityTypes: ['facility-group', 'facility'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'group-page',
    label: 'Group detail',
    description: 'One facility inside one group, on the group page the operator navigated from.',
    routePattern: '/portal/groups/{page}/{facilityGroupName}/{facilityGroupId}/facilityId/{facilityId}',
    entityTypes: ['facility-group', 'facility', 'utility-bill'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'groups-all',
    label: 'All groups',
    description: 'The full list of facility groups, with the facilities they contain and their headline measures.',
    routePattern: '/portal/groups/all',
    entityTypes: ['facility-group', 'facility'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'utility-provider-logins',
    label: 'Utility provider logins',
    description: 'The accounts this organisation holds with its utility providers, for one facility in a group.',
    routePattern: '/portal/groups/utility-provider-logins/{facilityGroupName}/{facilityGroupId}/facilityId/{facilityId}',
    entityTypes: ['utility-provider-login', 'utility-provider', 'facility', 'facility-group'],
    sensitive: true,
    highlightsAllowed: false,
  },
  {
    id: 'help-center',
    label: 'Help centre',
    description: 'Guidance for operators of this product, and how to reach support.',
    routePattern: '/portal/help-center',
    entityTypes: ['tutorial'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'map',
    label: 'Map',
    description: 'The portfolio on a map: facility positions and their headline measure.',
    routePattern: '/portal/map',
    entityTypes: ['facility', 'facility-group'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'onboarding-status',
    label: 'Onboarding status',
    description: 'The facilities still being brought into the product, with their outstanding steps.',
    routePattern: '/portal/onboarding',
    entityTypes: ['facility', 'utility-provider'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'onboarding-facility',
    label: 'Facility onboarding',
    description: 'One facility being brought into the product: its accounts, meters and outstanding steps.',
    routePattern: '/portal/onboarding/facilities/{facilityId}',
    entityTypes: ['facility', 'utility-provider', 'utility-bill'],
    routeEntityTypes: ['facility'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'report',
    label: 'Reports',
    description: 'The reporting area: the report to run, and the scope and period it covers.',
    routePattern: '/portal/report',
    entityTypes: ['report', 'facility', 'facility-group'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'report-charging-stations',
    label: 'Charging stations report',
    description: 'Usage and cost for electric-vehicle charging stations over the selected period.',
    routePattern: '/portal/report/charging-stations',
    entityTypes: ['charging-station', 'facility', 'report'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'report-detailed',
    label: 'Detailed report',
    description: 'Line-level energy and cost for the selected facilities and period.',
    routePattern: '/portal/report/detailed',
    entityTypes: ['report', 'facility', 'utility-bill'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    // Added by the 09-18 base sync, not by the original AC2 pass — the same shape as the two
    // 09-11 additions below. BRN-2451 landed this route on the default branch while this branch was
    // off it, so the registry was complete when it was written and silently incomplete afterwards —
    // no conflict marker, and the AC2 spec kept passing because the committed route inventory beside
    // it was equally stale. The producer reported it the moment it was re-run: 49 feature routes
    // became 50.
    //
    // It declares no entity type of its own, deliberately, and that is the difference from the two
    // 09-11 additions. The improvement projects and vendor bids this screen renders are committed
    // QA-only fixture data with no repository, no API and no catalog tool behind them, so declaring
    // a type for them would tell the engine this product can open something it cannot. What the
    // screen actually renders against is a report over facilities, which is what it declares.
    id: 'report-savings',
    label: 'Savings report',
    description: 'Measured savings for the efficiency projects this organisation has run, the facilities they were done at, and the vendor bids behind them.',
    routePattern: '/portal/report/savings',
    entityTypes: ['report', 'facility'],
    // Not sensitive, for the reason the header gives: the screen renders projects, savings figures
    // and vendor bids, not people or credentials. Classified with the other report surfaces.
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'report-summary',
    label: 'Summary report',
    description: 'Rolled-up energy, cost and emissions for the selected scope and period.',
    routePattern: '/portal/report/summary',
    entityTypes: ['report', 'facility', 'facility-group'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'settings-users',
    label: 'Users',
    description: 'The people with access to this product for this organisation, with their roles and permissions.',
    routePattern: '/portal/settings/users',
    entityTypes: ['user'],
    sensitive: true,
    highlightsAllowed: false,
  },
  {
    id: 'settings-users-audit-log',
    label: 'Audit log',
    description: 'Who did what in this product and when.',
    routePattern: '/portal/settings/users/audit-log',
    entityTypes: ['audit-event', 'user'],
    sensitive: true,
    highlightsAllowed: false,
  },
  {
    // Added by the 09-11 base sync, not by the original AC2 pass. BRN-2301 landed this route on the
    // default branch while this branch was off it, so the registry was complete when it was written
    // and silently incomplete afterwards — no conflict marker, and the AC2 spec kept passing because
    // the committed route inventory beside it was equally stale. The producer reported it the moment
    // it was re-run: 47 feature routes became 49.
    id: 'settings-users-conversion-factors',
    label: 'Conversion factors',
    description: 'The unit-conversion and emission constants this organisation bills and reports with, and where it differs from the platform default.',
    routePattern: '/portal/settings/users/conversion-factors',
    entityTypes: ['conversion-factor'],
    // Not sensitive, for the reason the header gives: the screen renders constants and their
    // defaults, not people or credentials. It sits beside Utility Rates and is classified with it.
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'settings-users-custom-fields',
    label: 'Custom fields',
    description: 'The extra fields this organisation defined for its own records, with the record kind they apply to and their retired-or-active state.',
    routePattern: '/portal/settings/users/custom-fields',
    entityTypes: ['custom-field', 'facility'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'settings-users-iam',
    label: 'Access management',
    description: 'The roles and permissions granted to people in this organisation.',
    routePattern: '/portal/settings/users/iam',
    entityTypes: ['user'],
    sensitive: true,
    highlightsAllowed: false,
  },
  {
    id: 'settings-users-mobileapp',
    label: 'Mobile access',
    description: 'Which people have mobile access and the devices they have enrolled.',
    routePattern: '/portal/settings/users/mobileapp',
    entityTypes: ['user'],
    sensitive: true,
    highlightsAllowed: false,
  },
  {
    id: 'settings-users-responsible-officials',
    label: 'Responsible officials',
    description: 'The people accountable for facilities and reports, with the facilities and reports they cover.',
    routePattern: '/portal/settings/users/responsible-officials',
    entityTypes: ['responsible-official', 'facility', 'report'],
    sensitive: true,
    highlightsAllowed: false,
  },
  {
    id: 'settings-users-tutorial-videos',
    label: 'Tutorial videos',
    description: 'The guidance material published to operators of this product.',
    routePattern: '/portal/settings/users/tutorial-videos',
    entityTypes: ['tutorial'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'settings-users-utility-rates',
    label: 'Utility rates',
    description: 'The tariffs facilities are billed under, with the facility and provider they apply to.',
    routePattern: '/portal/settings/users/utility-rates',
    entityTypes: ['utility-rate', 'facility', 'utility-provider'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'steam',
    label: 'Steam',
    description: 'Steam delivered to facilities over the selected period, and its cost.',
    routePattern: '/portal/steam',
    entityTypes: ['steam-meter', 'facility', 'utility-bill'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'tutorials',
    label: 'Tutorials',
    description: 'Walkthroughs of this product for operators.',
    routePattern: '/portal/tutorials',
    entityTypes: ['tutorial'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'utility-bills',
    label: 'Utility bills',
    description: 'Bills received from utilities, their period, their charges and their processing state.',
    routePattern: '/portal/utility-bills',
    entityTypes: ['utility-bill', 'facility', 'utility-provider'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'utility-bills-auto',
    label: 'Automated bill capture',
    description: 'Bills collected automatically from utility providers, with the results of the collection runs.',
    routePattern: '/portal/utility-bills/auto',
    entityTypes: ['utility-bill', 'utility-provider', 'facility'],
    sensitive: false,
    highlightsAllowed: true,
  },
  {
    id: 'utility-live-feed',
    label: 'Live feed',
    description: 'Near real-time interval readings arriving from monitored facilities.',
    routePattern: '/portal/utility-live-feed',
    entityTypes: ['live-feed-source', 'facility'],
    sensitive: false,
    highlightsAllowed: true,
  },
];
